import { TILE_SIZE } from '@shefira/shared';
import type Phaser from 'phaser';

/** Above the ground and its cover (props at -500), below every standing sprite. */
const MARKER_DEPTH = -400;
/**
 * White for a walk, yellow for a monster picked with Space, red for one
 * attacked, green for a townsperson walked up to (the smith).
 */
const MARKER_COLORS = {
  move: 0xffffff,
  target: 0xffd23b,
  attack: 0xff3b3b,
  talk: 0x5ce65c,
} as const;
export type MarkerKind = keyof typeof MARKER_COLORS;
/** One full fade out and back, as BrowserQuest's target square breathes. */
const PULSE_MS = 900;

/**
 * The square on the tile the local player clicked, like BrowserQuest's: white
 * where they walk to, red under the monster they attack.
 * Only this client draws it: it shows an intention sent, not a server fact.
 */
export class MoveMarker {
  private readonly graphics: Phaser.GameObjects.Graphics;
  private readonly pulse: Phaser.Tweens.Tween;
  private tile?: { x: number; y: number };
  private kind?: MarkerKind;

  constructor(scene: Phaser.Scene) {
    this.graphics = scene.add.graphics().setDepth(MARKER_DEPTH).setVisible(false);
    this.pulse = scene.tweens.add({
      targets: this.graphics,
      alpha: { from: 1, to: 0.35 },
      duration: PULSE_MS / 2,
      yoyo: true,
      repeat: -1,
      paused: true,
    });
  }

  /** What the square shows now, or nothing when hidden. */
  get shown(): MarkerKind | undefined {
    return this.tile === undefined ? undefined : this.kind;
  }

  show(tile: { x: number; y: number }, kind: MarkerKind): void {
    if (kind !== this.kind) this.draw(kind);
    this.place(tile);
    this.graphics.setVisible(true);
    this.graphics.setAlpha(1);
    this.pulse.restart();
    this.pulse.resume();
  }

  /** Follow a moving target without restarting the pulse. */
  place(tile: { x: number; y: number }): void {
    this.tile = tile;
    this.graphics.setPosition(tile.x * TILE_SIZE, tile.y * TILE_SIZE);
  }

  hide(): void {
    this.tile = undefined;
    this.graphics.setVisible(false);
    this.pulse.pause();
  }

  /** A walk's square is gone once the player stands on it. */
  arrive(at: { x: number; y: number }): void {
    if (this.kind === 'move' && this.tile?.x === at.x && this.tile.y === at.y) this.hide();
  }

  private draw(kind: MarkerKind): void {
    this.kind = kind;
    // Four filled edges rather than strokeRect: a 1 px stroke straddles the
    // edge and would land on half pixels (CLAUDE.md rule 6).
    this.graphics.clear();
    this.graphics.fillStyle(MARKER_COLORS[kind], 1);
    this.graphics.fillRect(0, 0, TILE_SIZE, 1);
    this.graphics.fillRect(0, TILE_SIZE - 1, TILE_SIZE, 1);
    this.graphics.fillRect(0, 1, 1, TILE_SIZE - 2);
    this.graphics.fillRect(TILE_SIZE - 1, 1, 1, TILE_SIZE - 2);
  }
}
